import { Box, useMediaQuery, useTheme } from "@mui/material";
import CustomAuthCardLayout from "global/components/CustomAuthCardLayout/CustomAuthCardLayout";
import { getCardContent } from "../ParticipantScreenHelper";
import { embedAllowsDownload } from "embed/embedMode";
import SigningInviteCard from "./SigningInviteCard";
import ApprovalInviteCard from "./ApprovalInviteCard";
import { useAppSelector } from "hooks/use-app-select";
import { RootState } from "utils/store";
import { useEffect, useState } from "react";
import { getDocumentUrl } from "screens/SignRequest/SignRequestService";
import { RequestParticipant, SignRequest } from "models/interfaces";
import strings from "global/constants/StringConstants";
import { isTruthy } from "helpers/methods";
import { needsSignerIdv } from "helpers/identityVerificationHelpers";
import IdentityVerificationContent from "./IdentityVerificationContent";

/** `requestStatus` while the signer still has to verify their identity. */
const IDENTITY_VERIFICATION_STATUS = "identityVerification";

const ShowRequestStatus = ({
  signRequest,
  reqToken,
  errorMessage,
  startSigning,
  loading,
  setLoading,
  downloadDocs,
  isDownloadingZip,
  setDialogType,
  setReasonOpen,
  setDelegationEmail,
  allowDelegation,
  onVerificationSuccess,
  handleApproveRejectRequest,
  identityVerificationRequired = false,
  onIdentityVerified,
}: {
  signRequest: SignRequest;
  reqToken: string;
  errorMessage: string;
  startSigning: () => void;
  loading: boolean;
  setLoading: Function;
  downloadDocs: () => void;
  isDownloadingZip?: boolean;
  setReasonOpen: (value: boolean) => void;
  setDialogType: (value: string) => void;
  setDelegationEmail: (value: string) => void;
  allowDelegation: boolean;
  onVerificationSuccess?: () => void | Promise<void>;
  handleApproveRejectRequest?: (isApprove: boolean) => void | Promise<void>;
  /**
   * A signer document call came back `403 IDV_REQUIRED:` — show verification
   * even if this copy of the request didn't say it was needed.
   */
  identityVerificationRequired?: boolean;
  /** Every IDV method passed: re-fetch and open the document. */
  onIdentityVerified?: () => void | Promise<void>;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [docUrl, setDocUrl] = useState<string>("");
  const { selectedParticipant } = useAppSelector(
    (state: RootState) => state.app,
  );
  const [docLoading, setDocLoading] = useState<boolean>(true);

  // Until it passes, the signer sees the verification card instead of the
  // document, and the preview URL (a gated document call) isn't requested.
  const isIdvPending =
    needsSignerIdv(signRequest?.signerIdentityVerification) ||
    identityVerificationRequired;

  let requestStatus = "";
  let reviewerStatus = "";
  let signerStatus = "";

  // Left blank until an actual decliner (reviewer/signer) is identified — the
  // declined card uses this to decide "You rejected…" vs "{name} declined…", so
  // defaulting it to the current user would mislabel non-decliners.
  let declinerName = "";
  let declinerEmail = "";

  const currentRole = (selectedParticipant as any)?.requestRole || "";

  if (signRequest) {
    const reviewerData = signRequest.approval?.reviewers?.find(
      (r: any) => r.email === selectedParticipant.email,
    );

    const signerData = signRequest.signers?.find(
      (s: any) => s.email === selectedParticipant.email,
    );

    reviewerStatus = reviewerData?.status || "";
    signerStatus = signerData?.status || "";
  }

  const rejectingReviewer = signRequest?.approval?.reviewers?.find(
    (r: any) => r?.status?.toLowerCase() === strings.rejected.toLowerCase(),
  );

  // A signer declining kills the request for everyone — the same way a reviewer
  // rejecting does. Detect any signer whose status is declined/rejected so all
  // participants land on the declined card, not a "your turn changed"/pending
  // view. Covers both the signer who declined and the others on the request.
  const decliningSigner = signRequest?.signers?.find(
    (s: any) =>
      s?.status?.toLowerCase() === strings.declined.toLowerCase() ||
      s?.status?.toLowerCase() === strings.rejected.toLowerCase(),
  );

  // Lockout: backend sets requestSecurity.failedAttempts on the current
  // signer/reviewer when verification fails. >= 5 means locked. Read from the
  // server-side signer record (signers[0] is server-filtered to the current
  // recipient) but fall back to selectedParticipant if needed.
  const currentSignerRecord = signRequest?.signers?.find(
    (s: any) => s.email === selectedParticipant.email,
  );
  const currentReviewerRecord = signRequest?.approval?.reviewers?.find(
    (r: any) => r.email === selectedParticipant.email,
  );
  // Take the highest reported count — verify responses update
  // selectedParticipant in Redux, but the embedded signers[0]/reviewers[]
  // records aren't refreshed until the next fetchPdfSignRequest. Max keeps
  // the lock sticky regardless of which slot reflects it.
  const failedAttemptsCandidates = [
    currentSignerRecord?.requestSecurity?.failedAttempts,
    currentReviewerRecord?.requestSecurity?.failedAttempts,
    (selectedParticipant as any)?.requestSecurity?.failedAttempts,
  ].filter((v): v is number => typeof v === "number");
  const failedAttempts =
    failedAttemptsCandidates.length > 0
      ? Math.max(...failedAttemptsCandidates)
      : 0;
  const isVerificationLocked =
    failedAttempts >= strings.verificationMaxAttempts;

  const isCcRecipient =
    currentRole?.toLowerCase() === "cc" ||
    (selectedParticipant as RequestParticipant)?.cc === true;

  if (isVerificationLocked) {
    requestStatus = strings.verificationLocked;
  } else if (isCcRecipient) {
    requestStatus = "ccView";
  } else if (
    signRequest.status === strings.signed ||
    signRequest.status === strings.voided ||
    signRequest.status === strings.expired ||
    signRequest.status === strings.declined ||
    signRequest.status === strings.rejected ||
    rejectingReviewer ||
    decliningSigner ||
    signRequest.status === strings.requestDelegatedMessage
  ) {
    requestStatus =
      signRequest.status === strings.rejected || rejectingReviewer
        ? strings.rejected
        : signRequest.status === strings.declined || decliningSigner
          ? strings.declined
          : signRequest.status;
    if (requestStatus === strings.rejected && rejectingReviewer) {
      declinerName = rejectingReviewer.name || "";
      declinerEmail = rejectingReviewer.email || "";
    }
    if (requestStatus === strings.declined && decliningSigner) {
      declinerName = decliningSigner.name || declinerName;
      declinerEmail = decliningSigner.email || declinerEmail;
    }
  } else if (currentRole === "Reviewer") {
    if (reviewerStatus === strings.approved) {
      requestStatus = strings.approved;
    } else if (
      reviewerStatus?.toLowerCase() === strings.decline?.toLowerCase() ||
      reviewerStatus?.toLowerCase() === strings.rejected.toLowerCase()
    ) {
      requestStatus = strings.rejected;
    } else {
      requestStatus = strings.inReview;
    }
  } else if (currentRole === "Signer") {
    const signerData = signRequest?.signers?.find(
      (s: any) => s.email === selectedParticipant.email,
    );
    const sec =
      (selectedParticipant as RequestParticipant)?.requestSecurity ??
      (signerData as RequestParticipant)?.requestSecurity;
    const needVerification =
      !!sec?.needSecuredAccess &&
      (sec?.isVerified === false || sec?.verified === false);

    if (needVerification) {
      requestStatus = "needVerification";
    } else if (signerStatus === strings.signed) {
      requestStatus = strings.waitingForOthers;
    } else if (isIdvPending) {
      // After the access code / OTP step, before the document.
      requestStatus = IDENTITY_VERIFICATION_STATUS;
    } else {
      const isDelegatedByError = errorMessage
        ?.toLocaleLowerCase()
        .includes(strings.requestDelegatedMessage.toLocaleLowerCase());
      const isNotTurn = errorMessage
        ?.toLocaleLowerCase()
        .includes(strings.notYourTurnErrorMessageV1.toLocaleLowerCase());
      requestStatus = isDelegatedByError
        ? strings.requestDelegatedMessage
        : isNotTurn
          ? strings.notYourTurnErrorMessageV1
          : strings.pending;
    }
  } else {
    requestStatus = signRequest.status;
  }

  useEffect(() => {
    if (!signRequest?.files || signRequest.files.length === 0) return;
    if (isIdvPending) return;
    const fetchUrl = async () => {
      try {
        const response = await getDocumentUrl(
          signRequest.files[0],
          true,
          reqToken!,
          true,
        );
        setDocUrl(response.message);
      } catch (e) {
        // ignore until data is ready
      }
    };
    fetchUrl();
  }, [signRequest?.files?.length, reqToken, isIdvPending]);

  return (
    <Box>
      {isTruthy(requestStatus) && (
        <CustomAuthCardLayout
          minWidth="30%"
          IsCustomAuthCardLayout={true}
          noCard={true}
          signerTheme
        >
          {requestStatus === IDENTITY_VERIFICATION_STATUS ? (
            <IdentityVerificationContent
              // Remount on a new state object so a re-fetch restarts cleanly.
              key={signRequest?.signerIdentityVerification?.status ?? "forced"}
              reqToken={reqToken}
              initialState={signRequest?.signerIdentityVerification}
              senderName={signRequest?.requestedBy?.name}
              onVerified={onIdentityVerified ?? startSigning}
            />
          ) : requestStatus === strings.pending ? (
            <SigningInviteCard
              signRequest={signRequest}
              selectedParticipant={selectedParticipant as RequestParticipant}
              reqToken={reqToken}
              onStartSigning={startSigning}
              allowDelegation={allowDelegation}
              setDialogType={setDialogType}
              setReasonOpen={setReasonOpen}
            />
          ) : requestStatus === strings.inReview ? (
            <ApprovalInviteCard
              signRequest={signRequest}
              reqToken={reqToken}
              onReview={startSigning}
              onApprove={() => handleApproveRejectRequest?.(true)}
              onReject={() => handleApproveRejectRequest?.(false)}
              loading={loading}
            />
          ) : (
            getCardContent(
            reqToken,
            theme,
            isMobile,
            requestStatus,
            downloadDocs,
            loading,
            setReasonOpen,
            setDialogType,
            docUrl,
            startSigning,
            allowDelegation,
            docLoading,
            setDocLoading,
            errorMessage,
            selectedParticipant as RequestParticipant,
            setLoading as (v: boolean) => void,
            onVerificationSuccess,
            declinerName,
            declinerEmail,
            isDownloadingZip,
            signRequest?.requestedBy?.name || "",
            signRequest?.requestedBy?.email || "",
            (() => {
              const firstDoc = signRequest?.files?.[0] as any;
              const raw = firstDoc?.unsignedDoc || firstDoc?.unsignedPdf || "";
              return raw.includes("/") ? raw.substring(raw.lastIndexOf("/") + 1) : raw;
            })(),
            signRequest?.autoGeneratedId
              ? `#${signRequest.autoGeneratedId}`
              : undefined,
            embedAllowsDownload(signRequest),
            )
          )}
        </CustomAuthCardLayout>
      )}
    </Box>
  );
};

export default ShowRequestStatus;
